import React, { useCallback, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { faArrowLeft, faDirections, faMap } from '@fortawesome/free-solid-svg-icons';
import { Button } from '../../ui';
import { MobileSheet } from '../../ui/components/MobileSheet';
import { useCoffeeShop, interpolateRatingColor } from './hooks/useCoffeeShops';
import { useShopYourHistory } from './hooks/useShopYourHistory';
import { useNearbyAlternatives } from './hooks/useNearbyAlternatives';
import { createMapPinIcon } from './components/MapPin';
import ChainBadge from './components/ChainBadge';
import ShopMapSnippet from './components/ShopMapSnippet';
import NearbyAlternativeCard from './components/NearbyAlternativeCard';
import ShopRatingsCollapsed from './components/ShopRatingsCollapsed';
import { openMapDirections } from './utils/mapLinks';
import { CoffeeMapBasemap, COFFEE_MAP_CONTAINER_CLASS } from './components/CoffeeMapBasemap';

const PULL_THRESHOLD_PX = 80;

const CoffeeShopDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data, loading, error, refetch } = useCoffeeShop(id);
    const yourHistory = useShopYourHistory(id);
    const nearby = useNearbyAlternatives(id);

    const [expandedMapOpen, setExpandedMapOpen] = useState(false);

    // pull-to-refresh
    const touchStartYRef = useRef<number | null>(null);
    const [pullPx, setPullPx] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const refreshAll = useCallback(async () => {
        setRefreshing(true);
        try {
            await Promise.all([refetch(), yourHistory.refetch(), nearby.refetch()]);
        } finally {
            setRefreshing(false);
        }
    }, [refetch, yourHistory, nearby]);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (window.scrollY <= 0 && e.touches.length === 1) {
            touchStartYRef.current = e.touches[0].clientY;
        } else {
            touchStartYRef.current = null;
        }
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (touchStartYRef.current == null) return;
        const delta = e.touches[0].clientY - touchStartYRef.current;
        if (delta > 0) setPullPx(Math.min(delta, 120));
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (touchStartYRef.current != null && pullPx > PULL_THRESHOLD_PX) {
            void refreshAll();
        }
        touchStartYRef.current = null;
        setPullPx(0);
    }, [pullPx, refreshAll]);

    const handleGetDirections = () => {
        if (!data?.coffeeShop) return;
        const { lat, lng } = data.coffeeShop.coordinates;
        openMapDirections(lat, lng, data.coffeeShop.name);
    };

    if (loading && !data) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-6">
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-10 h-10 border-4 border-muted border-t-sky rounded-full animate-spin mb-4" />
                    <p className="text-muted-foreground">Loading coffee shop...</p>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-6">
                <Button
                    onClick={() => navigate('/coffeeshops')}
                    variant="ghost"
                    icon={faArrowLeft}
                    className="mb-6"
                >
                    Back to Coffee Shops
                </Button>
                <div className="border-t-4 border-pink py-10 text-center">
                    <h2 className="text-2xl font-bold text-pink mb-2">Error</h2>
                    <p className="text-muted-foreground mb-4">{error || 'Coffee shop not found'}</p>
                    <Button onClick={refetch} variant="soft">Try Again</Button>
                </div>
            </div>
        );
    }

    const { coffeeShop, coffeeRatings } = data;
    const chainId = coffeeShop.chainId ?? undefined;
    const chainName = coffeeShop.chainName ?? undefined;

    const hist = yourHistory.data;
    const visits = hist?.count ?? 0;
    const yourLine = visits > 0
        ? `your avg here · ${hist?.avgRating?.toFixed(1)} across ${visits} ${visits === 1 ? 'visit' : 'visits'}`
        : 'you haven\'t rated here yet';

    const alts = nearby.data ?? [];
    const totalRatings = data.totalRatingsFound ?? coffeeShop.totalRatings ?? 0;
    const hasShopRating = totalRatings > 0;
    const ratingColor = hasShopRating ? interpolateRatingColor(coffeeShop.averageRating) : '#94a3b8';
    const showNearby = nearby.loading || alts.length > 0;

    // map rating tier to a brand pastel for the hero block
    const heroBgClass = !hasShopRating
        ? 'bg-paper'
        : coffeeShop.averageRating >= 8
            ? 'bg-mint/70'
            : coffeeShop.averageRating >= 6
                ? 'bg-lemon/70'
                : coffeeShop.averageRating >= 4
                    ? 'bg-gold/70'
                    : 'bg-pink/50';

    return (
        <div
            className="max-w-3xl mx-auto px-4 py-6"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {(pullPx > 0 || refreshing) && (
                <div
                    className="flex items-center justify-center text-sm text-muted-foreground mb-2"
                    style={{ height: refreshing ? 32 : Math.min(pullPx, 60) }}
                >
                    {refreshing ? (
                        <div className="w-5 h-5 border-2 border-muted border-t-sky rounded-full animate-spin" />
                    ) : pullPx > PULL_THRESHOLD_PX ? (
                        <span>Release to refresh</span>
                    ) : (
                        <span>Pull to refresh</span>
                    )}
                </div>
            )}

            <Button
                onClick={() => navigate('/coffeeshops')}
                variant="ghost"
                icon={faArrowLeft}
                className="mb-2"
            >
                Back
            </Button>

            {/* identity — brutal hero block: thick black border, hard offset shadow, brand pastel fill */}
            <header
                className={`${heroBgClass} border-brutal-thick border-ink shadow-brutal-md rounded-sm p-5 sm:p-6 mb-6`}
            >
                <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                        <h1 className="text-3xl sm:text-5xl font-black text-ink leading-[0.95] break-words">
                            {coffeeShop.name}
                        </h1>
                        <p className="text-ink/80 text-sm mt-3 font-medium">
                            {coffeeShop.address}
                        </p>
                        <p className="text-ink/70 text-sm">
                            {coffeeShop.city}, {coffeeShop.country}
                        </p>
                    </div>
                    {hasShopRating ? (
                        <div
                            className="shrink-0 flex flex-col items-center justify-center w-20 sm:w-24 h-20 sm:h-24 bg-paper border-brutal border-ink shadow-brutal-sm rounded-sm"
                            aria-label={`Average rating ${coffeeShop.averageRating.toFixed(1)} out of 10 from ${totalRatings} ratings`}
                        >
                            <span
                                className="text-3xl sm:text-4xl font-black leading-none"
                                style={{ color: ratingColor }}
                            >
                                {coffeeShop.averageRating.toFixed(1)}
                            </span>
                            <span className="text-[9px] sm:text-[10px] font-bold mt-1 uppercase tracking-widest text-ink/70">
                                /10 · {totalRatings}
                            </span>
                        </div>
                    ) : (
                        <div
                            className="shrink-0 flex flex-col items-center justify-center w-20 sm:w-24 h-20 sm:h-24 bg-paper border-brutal border-ink border-dashed shadow-brutal-sm rounded-sm"
                            aria-label="No ratings yet"
                        >
                            <span className="text-3xl font-black leading-none text-ink/40">—</span>
                            <span className="text-[9px] sm:text-[10px] font-bold mt-1 uppercase tracking-widest text-ink/60">
                                unrated
                            </span>
                        </div>
                    )}
                </div>

                {(chainId || visits > 0) && (
                    <div className="mt-5 flex flex-wrap items-center gap-2">
                        {chainId && (
                            <ChainBadge chainId={chainId} chainName={chainName} />
                        )}
                        {visits > 0 && (
                            <span className="inline-flex items-center px-3 py-1.5 bg-paper border-brutal border-ink shadow-brutal-sm rounded-sm text-xs font-bold uppercase tracking-wider text-ink">
                                you · {hist?.avgRating?.toFixed(1)} × {visits}
                            </span>
                        )}
                    </div>
                )}

                <div className="mt-5 flex flex-wrap gap-3">
                    <Button
                        onClick={handleGetDirections}
                        variant="default"
                        icon={faDirections}
                    >
                        Directions
                    </Button>
                    <Button
                        onClick={() => setExpandedMapOpen(true)}
                        variant="sky"
                        icon={faMap}
                    >
                        Expand map
                    </Button>
                    {(yourHistory.isStale || nearby.isStale) && !refreshing && (
                        <span className="self-center px-2 py-1 text-xs text-ink/70 font-medium uppercase tracking-wider">
                            refreshing…
                        </span>
                    )}
                </div>
            </header>

            {/* map snippet — brutal frame */}
            <div className="mb-6">
                <ShopMapSnippet
                    lat={coffeeShop.coordinates.lat}
                    lng={coffeeShop.coordinates.lng}
                    rating={coffeeShop.averageRating}
                    height={220}
                    onExpand={() => setExpandedMapOpen(true)}
                />
            </div>

            {/* nearby alternatives — only when there's something to show */}
            {showNearby && (
                <section className="mb-6">
                    <div className="flex items-baseline justify-between mb-3 px-1">
                        <h2 className="text-lg sm:text-xl font-black text-ink uppercase tracking-tight">
                            Nearby cafés
                        </h2>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-ink/60">
                            within ~1 km
                        </span>
                    </div>
                    {nearby.loading && alts.length === 0 ? (
                        <p className="text-sm text-ink/60 px-1">Looking for nearby spots…</p>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {alts.map(alt => (
                                <NearbyAlternativeCard
                                    key={alt.shopId}
                                    shopId={alt.shopId}
                                    name={alt.name}
                                    distanceMeters={alt.distanceMeters}
                                    avgRating={alt.avgRating}
                                    ratingCount={alt.ratingCount}
                                />
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* recent ratings */}
            <ShopRatingsCollapsed
                shopId={coffeeShop._id}
                coffeeRatings={coffeeRatings}
                totalRatings={totalRatings}
            />

            {/* expanded map sheet */}
            <MobileSheet
                open={expandedMapOpen}
                onOpenChange={setExpandedMapOpen}
                maxHeight="85vh"
                title={coffeeShop.name}
            >
                <div className="p-4">
                    <div
                        className="rounded-xl border border-ink/20 overflow-hidden relative z-0"
                        style={{ height: '60vh' }}
                    >
                        <MapContainer
                            center={[coffeeShop.coordinates.lat, coffeeShop.coordinates.lng]}
                            zoom={16}
                            className={COFFEE_MAP_CONTAINER_CLASS}
                            style={{ height: '100%', width: '100%' }}
                            scrollWheelZoom={true}
                        >
                            <CoffeeMapBasemap />
                            <Marker
                                position={[coffeeShop.coordinates.lat, coffeeShop.coordinates.lng]}
                                icon={L.icon({
                                    iconUrl: createMapPinIcon(coffeeShop.averageRating, true),
                                    iconSize: [40, 40],
                                    iconAnchor: [20, 20]
                                })}
                            />
                        </MapContainer>
                    </div>
                    <div className="mt-4 flex gap-3">
                        <Button
                            onClick={handleGetDirections}
                            variant="default"
                            icon={faDirections}
                            className="flex-1"
                        >
                            Get Directions
                        </Button>
                        <Button
                            onClick={() => setExpandedMapOpen(false)}
                            variant="soft"
                            className="flex-1"
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </MobileSheet>
        </div>
    );
};

export default CoffeeShopDetailPage;
